import { useState } from 'react';
import { Share2, Link2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FacebookIcon, XIcon } from '@/components/BrandIcons';
import { useToast } from '@/hooks/useToast';

interface ShareButtonProps {
  /** Absolute URL to share. Defaults to the current page URL. */
  url?: string;
  /** Title used by the native share sheet. */
  title?: string;
  /** Short text/summary used by the native share sheet. */
  text?: string;
}

export function ShareButton({ url, title, text }: ShareButtonProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const shareUrl =
    url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = title || 'Check out this post';

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: shareTitle, text, url: shareUrl });
    } catch {
      /* user cancelled or share failed — no-op */
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({ title: 'Link copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy link', variant: 'destructive' });
    }
  };

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareTitle,
  )}&url=${encodeURIComponent(shareUrl)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    shareUrl,
  )}`;

  // On devices with the native share sheet, a single tap is best.
  if (canNativeShare) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="rounded-full gap-2"
        onClick={handleNativeShare}
      >
        <Share2 className="size-4" />
        Share
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-2">
          <Share2 className="size-4" />
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={handleCopy} className="gap-2">
          {copied ? (
            <Check className="size-4 text-green-600" />
          ) : (
            <Link2 className="size-4" />
          )}
          {copied ? 'Copied!' : 'Copy link'}
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="gap-2">
          <a href={twitterHref} target="_blank" rel="noopener noreferrer">
            <XIcon className="size-4" />
            Share on X
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="gap-2">
          <a href={facebookHref} target="_blank" rel="noopener noreferrer">
            <FacebookIcon className="size-4" />
            Share on Facebook
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

